import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { Pathway } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    SummaryCrewUser,
    SummaryCrewUsersParams,
    VesselPathwaysParams,
    WatchlistVesselParams,
    WatchlistVesselPathwayStatistics,
} from '@routes/sidebar/admin-center/vessel-dashboard/___pages___/learning-dashboard/api/learningDashboard.api.ts';
import {
    getApiSummaryCrewUsers,
    getApiVesselPathways,
    getApiWatchlistVessel,
    postApiPathwayToVesselWatchlist,
    updateApiVesselWatchlistPathwayOrder,
} from '@routes/sidebar/admin-center/vessel-dashboard/___pages___/learning-dashboard/api/learningDashboard.api.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

interface MandatoryLearningStatusError {
    message?: string;
    [k: string]: any;
}

interface PathwayToVesselWatchlistParams extends VesselPathwaysParams {
    pathwayId: number;
}

interface VesselWatchlistPathwayOrderParams extends PathwayToVesselWatchlistParams {
    order: number;
    isDeleted: boolean;
}

interface VesselMandatoryLearningStatusState {
    error: MandatoryLearningStatusError | null;
    pathwaySummaries: PaginatedResponse<WatchlistVesselPathwayStatistics, EmptyResultsDescription> | null;
    summaryCrewUsers: Record<number, PaginatedResponse<SummaryCrewUser, number> | undefined>;
    pathways: Pathway[];
    summaryCrewUsersPathwayId: number | null;
    isGetPathwaysPending: boolean;
    isGetWatchlistVesselPending: boolean;
    isGetSummaryCrewUsersPending: boolean;
    isAddPathwayToWatchlistPending: boolean;
    isUpdateWatchlistPathwayOrderPending: boolean;
    isPathwaysModalOpened: boolean;
}

const initialState: VesselMandatoryLearningStatusState = {
    error: null,
    pathwaySummaries: null,
    summaryCrewUsers: [],
    pathways: [],
    summaryCrewUsersPathwayId: null,
    isGetPathwaysPending: false,
    isGetWatchlistVesselPending: false,
    isGetSummaryCrewUsersPending: false,
    isAddPathwayToWatchlistPending: false,
    isUpdateWatchlistPathwayOrderPending: false,
    isPathwaysModalOpened: false,
};

export const getVesselPathways = createAsyncThunk<
    Pathway[],
    VesselPathwaysParams,
    { rejectValue: MandatoryLearningStatusError }
>('vesselMandatoryLearningStatus/getVesselPathways', ({ tenantAlias, vesselId }, { rejectWithValue }) =>
    getApiVesselPathways({ tenantAlias, vesselId })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getWatchlistVessel = createAsyncThunk<
    PaginatedResponse<WatchlistVesselPathwayStatistics, EmptyResultsDescription>,
    WatchlistVesselParams,
    { rejectValue: MandatoryLearningStatusError }
>('vesselMandatoryLearningStatus/getWatchlistVessel', ({ tenantAlias, vesselId, payload }, { rejectWithValue }) =>
    getApiWatchlistVessel({ tenantAlias, vesselId, payload })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getSummaryCrewUsers = createAsyncThunk<
    PaginatedResponse<SummaryCrewUser, number>,
    SummaryCrewUsersParams,
    { rejectValue: MandatoryLearningStatusError }
>(
    'vesselMandatoryLearningStatus/getSummaryCrewUsers',
    ({ tenantAlias, vesselId, pathwayId, payload }, { rejectWithValue }) =>
        getApiSummaryCrewUsers({ tenantAlias, vesselId, pathwayId, payload })
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const addPathwayToVesselWatchlist = createAsyncThunk<
    void,
    PathwayToVesselWatchlistParams,
    { rejectValue: MandatoryLearningStatusError }
>(
    'vesselMandatoryLearningStatus/addPathwayToVesselWatchlist',
    ({ tenantAlias, vesselId, pathwayId }, { rejectWithValue }) =>
        postApiPathwayToVesselWatchlist({ tenantAlias, vesselId, pathwayId })
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const updateVesselWatchlistPathwayOrder = createAsyncThunk<
    void,
    VesselWatchlistPathwayOrderParams,
    { rejectValue: MandatoryLearningStatusError }
>(
    'vesselMandatoryLearningStatus/updateVesselWatchlistPathwayOrder',
    ({ tenantAlias, vesselId, pathwayId, order, isDeleted }, { rejectWithValue }) =>
        updateApiVesselWatchlistPathwayOrder({
            tenantAlias,
            vesselId,
            pathwayId,
            order,
            isDeleted,
        }).catch((err) => rejectWithValue(err)),
);

const vesselMandatoryLearningStatusSlice = createSlice({
    name: 'vesselMandatoryLearningStatus',
    initialState,
    reducers: {
        setIsPathwaysModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isPathwaysModalOpened = action.payload;
        },
        setReorderedPathways: (state, action: PayloadAction<WatchlistVesselPathwayStatistics[]>) => {
            if (state.pathwaySummaries?.data) {
                state.pathwaySummaries.data = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselPathways.pending, (state) => {
            state.isGetPathwaysPending = true;
        });
        builder.addCase(getVesselPathways.fulfilled, (state, action) => {
            state.isGetPathwaysPending = false;
            state.pathways = action.payload;
        });
        builder.addCase(getVesselPathways.rejected, (state, action) => {
            state.isGetPathwaysPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getWatchlistVessel.pending, (state) => {
            state.isGetWatchlistVesselPending = true;
        });
        builder.addCase(getWatchlistVessel.fulfilled, (state, action) => {
            state.isGetWatchlistVesselPending = false;
            state.pathwaySummaries = action.payload;
        });
        builder.addCase(getWatchlistVessel.rejected, (state, action) => {
            state.isGetWatchlistVesselPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getSummaryCrewUsers.pending, (state, action) => {
            state.summaryCrewUsersPathwayId = action.meta.arg.pathwayId;
            state.isGetSummaryCrewUsersPending = true;
        });
        builder.addCase(getSummaryCrewUsers.fulfilled, (state, action) => {
            state.isGetSummaryCrewUsersPending = false;
            state.summaryCrewUsersPathwayId = null;

            const pathwayId = action.meta.arg.pathwayId;

            state.summaryCrewUsers[pathwayId] = action.payload;
        });
        builder.addCase(getSummaryCrewUsers.rejected, (state, action) => {
            state.isGetSummaryCrewUsersPending = false;
            state.summaryCrewUsersPathwayId = null;
            state.error = extractError(action);
        });

        builder.addCase(addPathwayToVesselWatchlist.pending, (state) => {
            state.isAddPathwayToWatchlistPending = true;
        });
        builder.addCase(addPathwayToVesselWatchlist.fulfilled, (state) => {
            state.isAddPathwayToWatchlistPending = false;
        });
        builder.addCase(addPathwayToVesselWatchlist.rejected, (state, action) => {
            state.isAddPathwayToWatchlistPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselWatchlistPathwayOrder.pending, (state) => {
            state.isUpdateWatchlistPathwayOrderPending = true;
        });
        builder.addCase(updateVesselWatchlistPathwayOrder.fulfilled, (state) => {
            state.isUpdateWatchlistPathwayOrderPending = false;
        });
        builder.addCase(updateVesselWatchlistPathwayOrder.rejected, (state, action) => {
            state.isUpdateWatchlistPathwayOrderPending = false;
            state.error = extractError(action);
        });
    },
});

export const { setIsPathwaysModalOpened, setReorderedPathways } = vesselMandatoryLearningStatusSlice.actions;

export default vesselMandatoryLearningStatusSlice.reducer;
